import { useContext } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { useLocation, matchPath } from 'react-router-dom';
import { TournamentEntity } from 'app/interfaces/entity';
import { Route } from 'app/enums';
import { CreateTournamentFormFields } from 'modules/tournaments/interfaces/forms';
import {
  calculateSingleCurrencyTournamentPrizes,
  calculateTournamentPlatformFees,
  calculateTournamentPrizes,
} from 'modules/game-payments/utils';
import { useTournamentPlatformFeePercentage } from 'shared/hooks/use-platform-fee-percentage';
import {
  getActiveTeamsCount,
  getPlayersJoinedWithRewardBalance,
} from './use-tournament-team-details';
import { GameActivityPaymentContext } from 'modules/game-payments/providers';

const collectValuesForCalculation = (
  source: UseFormWatch<CreateTournamentFormFields> | TournamentEntity
) => {
  if (typeof source === 'function') {
    return source([
      'prizeAndDistribution.seedAmount',
      'prizeAndDistribution.playerEntryFee',
      'prizeAndDistribution.hostFeesPercentage',
      'teamsSettings.teamSize',
      'teamsSettings.minTeams',
      'teamsSettings.maxTeams',
    ]);
  }

  const activeTeamsCount = Boolean(source?.teams) ? getActiveTeamsCount(source) : 0;
  const playersToExcludeCount = Boolean(source?.teams)
    ? getPlayersJoinedWithRewardBalance(source).length
    : 0;

  return [
    source?.prizeAndDistribution?.seedAmount,
    source?.prizeAndDistribution?.playerEntryFee,
    source?.prizeAndDistribution?.hostFeesPercentage,
    source?.teamsSettings?.teamSize,
    source?.teamsSettings?.minTeams,
    source?.teamsSettings?.maxTeams,
    activeTeamsCount,
    playersToExcludeCount,
  ];
};

const collectGameId = (
  source: UseFormWatch<CreateTournamentFormFields> | TournamentEntity
): string => {
  if (typeof source === 'function') {
    return source('gameId');
  }

  return source?.game?.id ?? '';
};

export const useSingleCurrencyPrizeAndDistributionStatistic = (
  object: UseFormWatch<CreateTournamentFormFields> | TournamentEntity
) => {
  const [
    seedAmount = 0,
    playerEntryFee = 0,
    hostFeesPercentage = 0,
    teamSize,
    minTeams,
    maxTeams,
    currentTeamsCount = 1,
  ] = collectValuesForCalculation(object);

  const gameId = collectGameId(object);
  const { platformFeePercentage } = useTournamentPlatformFeePercentage(gameId);

  const location = useLocation();
  const isOnCreateTournament = Boolean(matchPath(Route.CreateTournament, location.pathname));

  return calculateSingleCurrencyTournamentPrizes({
    teamSize,
    minTeams,
    maxTeams,
    currentTeamsCount,
    seedAmount,
    entryFee: playerEntryFee,
    hostFeePercentage: hostFeesPercentage,
    platformFeePercentage,
    isPlatformFeeInclusiveIntoSeedAmount: isOnCreateTournament,
  });
};

export const usePrizeAndDistributionStatistic = (
  object: UseFormWatch<CreateTournamentFormFields> | TournamentEntity
) => {
  const [
    seedAmount = 0,
    playerEntryFee = 0,
    hostFeesPercentage = 0,
    teamSize,
    minTeams,
    maxTeams,
    currentTeamsCount = 1,
    playersToExcludeCount = 0,
  ] = collectValuesForCalculation(object);

  return calculateTournamentPrizes({
    teamSize,
    minTeams,
    maxTeams,
    currentTeamsCount,
    seedAmount,
    entryFee: playerEntryFee,
    hostFeePercentage: hostFeesPercentage,
    playersToExcludeCount,
  });
};

export const useTournamentPlatformFees = (
  object: UseFormWatch<CreateTournamentFormFields> | TournamentEntity
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [seedAmount = 0, entryFee = 0, hostFeesPercentage, teamSize, minTeams, maxTeams] =
    collectValuesForCalculation(object);

  const { paymentConfig, platformFeePercentage } = useContext(GameActivityPaymentContext);

  return calculateTournamentPlatformFees({
    teamSize,
    minTeams,
    maxTeams,
    seedAmount,
    entryFee,
    feePercentage: platformFeePercentage,
    exchangeRate: paymentConfig.exchangeRate,
  });
};
